import React, { useEffect, useRef } from 'react';

const FIRST   = ['Priya','Arjun','Sneha','Rohit','Anita','Vikram','Pooja','Rahul','Deepika','Amit',
                 'Kavya','Suresh','Meera','Rajesh','Nisha','Karthik','Divya','Sanjay','Riya','Mohit'];
const LAST    = ['Sharma','Patel','Reddy','Kumar','Singh','Joshi','Nair','Gupta','Iyer','Shah',
                 'Malhotra','Kapoor','Verma','Mehta','Rao','Bose','Chawla','Desai','Khanna','Pandey'];
const CITIES  = ['Mumbai','Delhi','Bangalore','Chennai','Hyderabad','Pune','Kolkata','Ahmedabad','Jaipur','Surat'];
const AGES    = ['Gen Z','Millennial','Gen X'];
const GENDERS = ['Female','Male'];
const CATS    = ['Beauty','Skincare','Fashion','FMCG','Electronics','Food & Bev','Wellness','Apparel','Home Décor'];
const BEHAVIORS = {
    'Beauty':      ['Trend-follower. Buys based on influencer picks and social proof.','Impulse buyer. Attracted to limited-edition drops and viral products.','Brand-loyal. Sticks to 2–3 trusted labels and repurchases regularly.'],
    'Skincare':    ['Research-driven. Reads reviews carefully before buying premium products.','Ingredient-conscious. Prioritises clean formulas over brand names.','Routine-builder. Purchases in sets and follows dermatologist advice.'],
    'Fashion':     ['Style-conscious. Shops seasonal sales and follows fast-fashion trends.','Occasion-driven. Buys for events and rarely repurchases the same item.','Sustainable shopper. Prefers slow fashion and ethical brands.'],
    'FMCG':        ['Value-driven. Prefers trusted brands and bulk buying for household needs.','Convenience-first. Shops weekly and sticks to a fixed grocery list.','Deal-hunter. Switches brands based on discounts and combo offers.'],
    'Electronics': ['Tech-savvy. Early adopter who compares specs across brands before buying.','Research-heavy. Watches review videos and waits for price drops.','Ecosystem-loyal. Stays within one brand family for all devices.'],
    'Food & Bev':  ['Convenience-first. Orders online frequently and prefers quick delivery.','Foodie. Tries new cuisines and spends generously on dining experiences.','Health-aware. Reads nutrition labels and prefers low-sugar options.'],
    'Wellness':    ['Health-conscious. Prioritises organic and natural products over price.','Value-seeker. Prefers bundled deals, cashback, and loyalty programs.','Routine-driven. Buys supplements and fitness gear on a monthly cycle.'],
    'Apparel':     ['Brand-loyal. Sticks to 2–3 preferred brands and shops end-of-season sales.','Comfort-first. Prioritises fit and fabric over style trends.','Occasion buyer. Purchases for specific events and rarely shops casually.'],
    'Home Décor':  ['Value-seeker. Prefers bundled deals, cashback, and loyalty reward programs.','Aesthetic-driven. Curates a consistent home style across all purchases.','Practical buyer. Focuses on durability and functionality over looks.'],
};

const PALETTE = {
    'Beauty':'#9333EA','Skincare':'#7C3AED','Fashion':'#2563EB','FMCG':'#0891B2',
    'Electronics':'#0D9488','Food & Bev':'#D97706','Wellness':'#16A34A',
    'Apparel':'#DC2626','Home Décor':'#EA580C',
};

function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

function hexToRgb(hex) {
    hex = hex.trim().replace('#','');
    if (hex.length === 3) hex = hex.split('').map(c => c+c).join('');
    return {
        r: parseInt(hex.slice(0,2),16),
        g: parseInt(hex.slice(2,4),16),
        b: parseInt(hex.slice(4,6),16),
    };
}

const GLOBE_PROFILES = Array.from({ length: 24 }, () => {
    const fn     = pick(FIRST), ln = pick(LAST);
    const gender = pick(GENDERS);
    const age    = pick(AGES);
    const city   = pick(CITIES);
    const cat    = pick(CATS);
    return {
        initials: fn[0] + ln[0],
        name:     fn + ' ' + ln,
        role:     'Consumer Persona · ' + cat,
        city, cat, gender, age,
        color:    PALETTE[cat] || '#7C3AED',
        behavior: pick(BEHAVIORS[cat]),
    };
});

const ConsumerGlobe = () => {
    const canvasRef      = useRef(null);
    const wrapperRef     = useRef(null);
    const cardRef        = useRef(null);
    const animFrameRef   = useRef(null);
    const activeIdxRef   = useRef(0);
    const cycleTimerRef  = useRef(null);

    useEffect(() => {
        const canvas  = canvasRef.current;
        const card    = cardRef.current;
        const wrapper = wrapperRef.current;
        if (!canvas || !card || !wrapper) return;

        const ctx = canvas.getContext('2d');

        // Read accent color from CSS variable so it respects theme switches
        const accentHex = getComputedStyle(document.documentElement)
            .getPropertyValue('--orange').trim() || '#22C55E';
        const ac = hexToRgb(accentHex);
        const acRgba = (a) => `rgba(${ac.r},${ac.g},${ac.b},${a})`;

        function resize() {
            const rect = canvas.parentElement.getBoundingClientRect();
            canvas.width  = rect.width  * 1.5;
            canvas.height = rect.height * 1.5;
            canvas.style.width  = rect.width  + 'px';
            canvas.style.height = rect.height + 'px';
        }
        resize();

        const N = 900;
        const golden = Math.PI * (3 - Math.sqrt(5));
        const pts = Array.from({ length: N }, (_, i) => {
            const y = 1 - (i / (N - 1)) * 2;
            const r = Math.sqrt(1 - y * y);
            const t = golden * i;
            return { x: Math.cos(t) * r, y, z: Math.sin(t) * r };
        });

        // Spread 24 profile dots evenly across the sphere
        const profileIndices = Array.from({ length: 24 }, (_, i) =>
            Math.floor((i / 24) * N + Math.floor(Math.random() * (N / 24)))
        );

        let angle      = 0;
        let pulsePhase = 0;

        // ── Card helpers ──────────────────────────────────────────────
        function updateCard(idx) {
            const p = GLOBE_PROFILES[idx];
            const av = card.querySelector('.gc-avatar');
            av.textContent  = p.initials;
            av.style.background = `linear-gradient(135deg, ${p.color}, ${p.color}99)`;
            card.querySelector('.gc-name').textContent = p.name;
            card.querySelector('.gc-role').textContent = p.role;
            card.querySelector('.gc-city').textContent = p.city;
            card.querySelector('.gc-behavior').textContent = p.behavior;
            card.querySelector('.gc-tags').innerHTML = [
                '📍 ' + p.city,
                p.gender === 'Female' ? '👩 Female' : '👨 Male',
                '⭐ ' + p.age,
                '🛍 ' + p.cat,
            ].map(t => `<span class="gc-tag">${t}</span>`).join('');
        }

        function positionCard(sx, sy) {
            // sx/sy are canvas pixels (1.5× DPR); convert to CSS pixels
            const cssX = sx / 1.5;
            const cssY = sy / 1.5;
            const wW   = wrapper.offsetWidth;
            const wH   = wrapper.offsetHeight;
            const CW   = 220, CH = 175;

            let x = cssX + 18;
            let y = cssY - CH / 2;

            if (x + CW > wW - 8) x = cssX - CW - 18;
            if (x < 8)           x = 8;
            if (y + CH > wH - 8) y = wH - CH - 8;
            if (y < 8)           y = 8;

            card.style.left = x + 'px';
            card.style.top  = y + 'px';
        }

        // ── Draw loop ─────────────────────────────────────────────────
        function draw() {
            const W = canvas.width, H = canvas.height;
            ctx.clearRect(0, 0, W, H);

            const radius = Math.min(W, H) * 0.40;
            const cx = W / 2, cy = H / 2;
            const cosA = Math.cos(angle), sinA = Math.sin(angle);

            const activeGlobePt = profileIndices[activeIdxRef.current];

            const projected = pts.map((p, i) => {
                const rx  = p.x * cosA - p.z * sinA;
                const rz  = p.x * sinA + p.z * cosA;
                const ry  = p.y;
                const fov = 2.8;
                const scale = fov / (fov + rz);
                return {
                    sx: cx + rx * radius * scale,
                    sy: cy + ry * radius * scale,
                    depth: rz, scale,
                    edgeDist: Math.sqrt(rx * rx + ry * ry),
                    idx: i,
                };
            }).sort((a, b) => a.depth - b.depth);

            // Rim ring
            ctx.save();
            ctx.beginPath();
            ctx.arc(cx, cy, radius * 1.02, 0, Math.PI * 2);
            ctx.strokeStyle = 'rgba(255,255,255,0.12)';
            ctx.lineWidth = 2;
            ctx.stroke();
            const rimGlow = ctx.createRadialGradient(cx, cy, radius * 0.92, cx, cy, radius * 1.15);
            rimGlow.addColorStop(0,    'rgba(255,255,255,0)');
            rimGlow.addColorStop(0.7,  'rgba(255,255,255,0.04)');
            rimGlow.addColorStop(0.85, 'rgba(255,255,255,0.08)');
            rimGlow.addColorStop(1,    'rgba(255,255,255,0)');
            ctx.beginPath();
            ctx.arc(cx, cy, radius * 1.15, 0, Math.PI * 2);
            ctx.fillStyle = rimGlow;
            ctx.fill();
            ctx.restore();

            let activePos = null;

            projected.forEach(p => {
                const isActive  = p.idx === activeGlobePt;

                const rimFactor  = Math.min(p.edgeDist / 0.95, 1);
                const depthFactor = (p.depth + 1) / 2;
                const baseAlpha  = 0.08 + depthFactor * 0.55;
                const rimBoost   = rimFactor > 0.75 ? (rimFactor - 0.75) * 4 : 0;
                const alpha      = Math.min(baseAlpha + rimBoost * 0.5, 1);
                const r          = p.scale * (2.8 + rimBoost * 1.8);

                if (isActive) {
                    // Track visible position for card placement
                    if (depthFactor > 0.35) activePos = { sx: p.sx, sy: p.sy };

                    // Pulsing glow ring
                    const pulse  = 0.5 + 0.5 * Math.sin(pulsePhase);
                    const pulseR = r * (4 + pulse * 5) * 1.5;
                    const pGlow  = ctx.createRadialGradient(p.sx, p.sy, r, p.sx, p.sy, pulseR);
                    pGlow.addColorStop(0, acRgba(0.35 * (1 - pulse * 0.3)));
                    pGlow.addColorStop(1, acRgba(0));
                    ctx.beginPath();
                    ctx.arc(p.sx, p.sy, pulseR, 0, Math.PI * 2);
                    ctx.fillStyle = pGlow;
                    ctx.fill();

                    // Bright accent dot
                    ctx.beginPath();
                    ctx.arc(p.sx, p.sy, r * 3, 0, Math.PI * 2);
                    ctx.fillStyle = acRgba(Math.min(alpha * 1.8, 1));
                    ctx.fill();

                } else {
                    // Regular globe dot with rim glow
                    if (rimFactor > 0.85 && depthFactor > 0.3) {
                        const glowR = r * 3;
                        const glow  = ctx.createRadialGradient(p.sx, p.sy, r * 0.5, p.sx, p.sy, glowR);
                        glow.addColorStop(0, `rgba(255,255,255,${alpha * 0.3})`);
                        glow.addColorStop(1, 'rgba(255,255,255,0)');
                        ctx.beginPath();
                        ctx.arc(p.sx, p.sy, glowR, 0, Math.PI * 2);
                        ctx.fillStyle = glow;
                        ctx.fill();
                    }
                    ctx.beginPath();
                    ctx.arc(p.sx, p.sy, r, 0, Math.PI * 2);
                    ctx.fillStyle = `rgba(220,220,220,${alpha})`;
                    ctx.fill();
                }
            });

            // Move card to follow the active dot
            if (activePos) positionCard(activePos.sx, activePos.sy);

            angle      += 0.003;
            pulsePhase += 0.06;
            animFrameRef.current = requestAnimationFrame(draw);
        }

        // ── Auto-cycle profiles ───────────────────────────────────────
        updateCard(0);
        card.style.opacity = '1';

        function cycleProfile() {
            card.style.opacity = '0';
            setTimeout(() => {
                activeIdxRef.current = (activeIdxRef.current + 1) % GLOBE_PROFILES.length;
                updateCard(activeIdxRef.current);
                card.style.opacity = '1';
            }, 350);
            cycleTimerRef.current = setTimeout(cycleProfile, 3000);
        }
        cycleTimerRef.current = setTimeout(cycleProfile, 3000);

        window.addEventListener('resize', resize);
        draw();

        return () => {
            window.removeEventListener('resize', resize);
            if (animFrameRef.current)  cancelAnimationFrame(animFrameRef.current);
            if (cycleTimerRef.current) clearTimeout(cycleTimerRef.current);
        };
    }, []);

    return (
        <div ref={wrapperRef} style={{ position: 'relative', width: '100%', height: '100%' }}>
            <canvas ref={canvasRef} id="sphere-canvas" style={{ display: 'block' }} />
            <div ref={cardRef} className="globe-card">
                <div className="globe-card-inner">
                    <div className="gc-header">
                        <div className="gc-avatar"></div>
                        <div className="gc-info">
                            <div className="gc-name"></div>
                            <div className="gc-role"></div>
                        </div>
                    </div>
                    <div className="gc-city"></div>
                    <div className="gc-behavior"></div>
                    <div className="gc-tags"></div>
                </div>
            </div>
        </div>
    );
};

export default ConsumerGlobe;